/* Tork SEO — listicle tool dataset (real providers, verifiable facts as of June 2026).
 * Used to build the ranked picks + comparison tables on /best/* listicles.
 * HONESTY: real competitors, real facts. Tork is the Editor's Pick #1 on the stated criteria
 * (Done-With-You + all-in-one + lowest entry price for a local business owner); each tool's
 * "bestFor" is honest about who it genuinely suits better. Re-verify pricing quarterly.
 */
export type Tool = {
  key: string;
  name: string;
  isClient?: boolean;
  tagline: string;
  bestFor: string;
  standout: string;   // one verifiable, checkable line (rule #3)
  price: string;
  model: string;
  href?: string;      // internal link (alt/compare page) where we have one
};

// Default ranking order (index = rank). Tork #1 as the all-in-one Done-With-You pick.
export const TOOLS: Tool[] = [
  {
    key: "tork", name: "Tork", isClient: true,
    tagline: "The Done-With-You AI local SEO platform — it tells you exactly what to do *and* does the heavy lifting (GBP optimization, signature posts, reviews, geo-grid authority).",
    bestFor: "local & multi-location business owners who want the agency outcome without hiring an agency",
    standout: "Built from a $2,500–$10,000/mo done-for-you system that's helped 5,000+ businesses rank; backed by 35+ years of combined experience.",
    price: "$1 for a 3-day trial, then a founding-member rate. Cancel in one click.",
    model: "Done-With-You (guide + automate)",
    href: "/ai-local-seo-software",
  },
  {
    key: "merchynt", name: "Merchynt (Paige AI)",
    tagline: "An AI agent (\"Paige\") that automates Google Business Profile optimization — posts, services, descriptions, and attributes.",
    bestFor: "hands-on owners who want AI GBP automation and are comfortable steering their own strategy",
    standout: "AI GBP automation with optional citation building; well-rated (~4.8) but per-profile pricing and add-ons stack.",
    price: "$99/mo per Google Business Profile; citations a $40/mo add-on.",
    model: "Tool-first automation",
    href: "/alternatives/merchynt",
  },
  {
    key: "brightlocal", name: "BrightLocal",
    tagline: "The trusted all-in-one local SEO reporting platform — rank tracking, audits, and citation tools.",
    bestFor: "agencies and SEOs who need deep reporting and white-label rank tracking",
    standout: "Industry-standard local rank tracking and audits; pay-as-you-go citation building.",
    price: "$39–$59/mo by tier; citations priced per submission.",
    model: "Reporting platform",
    href: "/alternatives/brightlocal",
  },
  {
    key: "localo", name: "Localo",
    tagline: "An affordable AI tool that suggests Google Business Profile optimization tasks for single-location businesses.",
    bestFor: "budget-minded single-location owners who'll execute the suggested tasks themselves",
    standout: "Free entry tier plus a clean AI task interface for GBP optimization.",
    price: "Free tier; paid plans from ~$39/mo.",
    model: "AI task suggestions",
    href: "/alternatives/localo",
  },
  {
    key: "whitespark", name: "Whitespark",
    tagline: "The gold standard for citation building, sold as focused specialist tools.",
    bestFor: "SEO specialists who want the most accurate citation building, à la carte",
    standout: "Best-in-class citation accuracy; tools (rank tracker, citation finder, reputation builder) sold separately.",
    price: "À la carte, roughly $14–$200/mo per tool.",
    model: "À-la-carte specialist tools",
    href: "/alternatives/whitespark",
  },
  {
    key: "birdeye", name: "Birdeye",
    tagline: "An enterprise reputation and customer-experience platform — reviews, messaging, and surveys at scale.",
    bestFor: "multi-location and enterprise brands that need reputation management at scale",
    standout: "Powerful enterprise reputation/CX suite; typically sold on annual contracts.",
    price: "Custom, typically ~$300+/mo on annual contracts.",
    model: "Enterprise reputation suite",
    href: "/alternatives/birdeye",
  },
  {
    key: "podium", name: "Podium",
    tagline: "A messaging and reviews platform built to convert leads you already have via texting.",
    bestFor: "businesses focused on converting inbound leads through two-way texting",
    standout: "Strong texting/lead-inbox and review-request workflows.",
    price: "From ~$399/mo; contract-based.",
    model: "Messaging + reviews suite",
    href: "/alternatives/podium",
  },
  {
    key: "jumper", name: "Jumper Media",
    tagline: "A done-for-you agency known for Instagram growth and social content, with local SEO as one service.",
    bestFor: "businesses that want hands-off Instagram and social content management",
    standout: "3,000+ small-business clients; agency-managed social and local services.",
    price: "~$100–$10,000 depending on services.",
    model: "Done-for-you agency",
    href: "/alternatives/jumper-media",
  },
];

export const toolByKey = (k: string): Tool => {
  const t = TOOLS.find((x) => x.key === k);
  if (!t) throw new Error("Unknown tool key: " + k);
  return t;
};
